// The inspection page's rules — which view a pallet belongs to, what each view
// counts, the order the cards come in, finding the pallet in front of you, and
// what a tick says. Kept out of the page so each rule is tested on its own
// (F03 UF-01..04).
//
// One queue read feeds every view (get_inspection_dashboard, mig
// 20260714130000): the pallets in the loading area and the approved pallets
// not yet on a truck, plus the recent PPECB consignments. The page used to
// show every one of those pallets on one list — pallets still to inspect,
// pallets already with PPECB and pallets already approved — so an approved
// card stayed under the one being looked for, and the header counted all
// three as "awaiting inspection".
import type { ReadState } from '@/lib/readState'
import { isValidSscc, normaliseSscc } from '@/lib/sscc'
import { ValidationError } from '@/lib/errorReporter'
import { inspectionAge } from '@/lib/inspectionAge'

export type InspectionView = 'inspect' | 'send' | 'ppecb' | 'approved'
export const INSPECTION_VIEWS: readonly InspectionView[] = ['inspect', 'send', 'ppecb', 'approved']

/** The queue-row fields these rules read. */
export interface QueueRow {
  pallet_id:            string
  status:               string
  channel:              'EXPORT' | 'LOCAL' | null
  target_market:        string | null
  pallet_type:          string | null
  ppecb_consignment_id: string | null
  packed_at:            string | null
  stocked_at:           string | null
}

/** A queue row as the page holds it: the RPC's pallet plus its run's variety. */
export interface QueuePallet extends QueueRow {
  grade:                  string | null
  size_code:              string | null
  net_weight_kg:          number | null
  runs:                   { variety: string | null } | null
  coldroom_grid:          string | null
  run_id:                 string | null
  ppecb_rejection_code:   string | null
  ppecb_rejection_reason: string | null
  version:                number
}

/** The three views a queue row can sit on (Send to PPECB is a subset of To inspect). */
export type QueueView = 'inspect' | 'ppecb' | 'approved'

/**
 * Where a queue row lives. A pallet sent to PPECB stays IN_LOADING_AREA while
 * TITAN decides, so its status alone cannot tell it from one nobody has
 * touched — the consignment id does. A recall clears that id, so a recalled
 * pallet is back on To inspect.
 */
export function queueViewOf(p: Pick<QueueRow, 'status' | 'ppecb_consignment_id'>): QueueView {
  if (p.status === 'INSPECTED_APPROVED') return 'approved'
  if (p.ppecb_consignment_id) return 'ppecb'
  return 'inspect'
}

/**
 * When a pallet's clock started: its cold-room stamp, or — for one sent
 * straight to the loading bay, which never gets `stocked_at` — when it was
 * packed. The card's 48 h clock already ran off this; the queue's ORDER BY
 * stocked_at did not, so the OLDEST pallets (no stamp) sank to the bottom.
 */
function ageStart(p: Pick<QueueRow, 'stocked_at' | 'packed_at'>): string | null {
  return p.stocked_at ?? p.packed_at
}

/** Oldest clock first; a pallet with neither time last; ties by pallet id. */
export function byAge(a: QueueRow, b: QueueRow): number {
  const ta = Date.parse(ageStart(a) ?? '')
  const tb = Date.parse(ageStart(b) ?? '')
  const ka = Number.isNaN(ta) ? Number.POSITIVE_INFINITY : ta
  const kb = Number.isNaN(tb) ? Number.POSITIVE_INFINITY : tb
  if (ka !== kb) return ka < kb ? -1 : 1
  return a.pallet_id < b.pallet_id ? -1 : a.pallet_id > b.pallet_id ? 1 : 0
}

/** Every queue row on its view, each view oldest first (see {@link byAge}). */
export function splitQueue<T extends QueueRow>(rows: readonly T[]): Record<QueueView, T[]> {
  const out: Record<QueueView, T[]> = { inspect: [], ppecb: [], approved: [] }
  for (const p of rows) out[queueViewOf(p)].push(p)
  out.inspect.sort(byAge)
  out.ppecb.sort(byAge)
  out.approved.sort(byAge)
  return out
}

/**
 * An export pallet that can go to PPECB: in the loading area, carrying a
 * market, and not already on a consignment. PPECB takes one market per
 * submission, so these are grouped by market on the Send to PPECB view.
 * Every one of them is also still on To inspect.
 */
export function isSubmittable(p: QueueRow): boolean {
  return !!p.target_market
    && !p.ppecb_consignment_id
    && p.status === 'IN_LOADING_AREA'
    && (p.pallet_type === 'Export Pallet' || p.pallet_type === 'Export' || p.channel === 'EXPORT')
}

/** Submittable pallets, one group per market, markets A→Z. */
export function groupByMarket<T extends QueueRow>(rows: readonly T[]): Array<[market: string, pallets: T[]]> {
  const groups = new Map<string, T[]>()
  for (const p of rows) {
    const k = (p.target_market ?? '').trim() || '—'
    const g = groups.get(k)
    if (g) g.push(p)
    else groups.set(k, [p])
  }
  return [...groups.entries()].sort((a, b) => a[0].localeCompare(b[0]))
}

/** How many pallets each view holds. */
export interface ViewCounts { inspect: number; send: number; ppecb: number; approved: number }

export function viewCounts(rows: readonly QueueRow[]): ViewCounts {
  const split = splitQueue(rows)
  return {
    inspect:  split.inspect.length,
    send:     split.inspect.filter(isSubmittable).length,
    ppecb:    split.ppecb.length,
    approved: split.approved.length,
  }
}

/**
 * The pipeline strip's three numbers. The server counts every loading-area
 * pallet as "ready to inspect"; the To inspect tab leaves out those already on
 * a PPECB group, so the strip took 14 beside a tab of 12. The strip's last
 * number is taken from the same queue the tab counts.
 */
export function pipelineStrip(
  pipeline: Record<string, number>, rows: readonly QueueRow[],
): { IN_PRODUCTION: number; IN_COLDROOM: number; IN_LOADING_AREA: number } {
  return {
    IN_PRODUCTION: pipeline.IN_PRODUCTION ?? 0,
    IN_COLDROOM: pipeline.IN_COLDROOM ?? 0,
    IN_LOADING_AREA: viewCounts(rows).inspect,
  }
}

/**
 * A count to print beside a tab or a filter: the number once the queue has
 * been read, a dash when it could not be (a dash is not a zero), and nothing
 * while the first read is in flight — the page used to print "0 pallets" and
 * "All (0)" in that moment, which on packhouse wifi reads as an empty queue.
 */
export function countLabel(state: ReadState, n: number): string | undefined {
  if (state === 'usable') return String(n)
  if (state === 'unread') return '—'
  return undefined
}

export const pallets = (n: number) => `${n} pallet${n === 1 ? '' : 's'}`

/**
 * Hours on the pallet's clock ({@link ageStart}); 0 when neither time is known.
 */
export function ageHours(p: Pick<QueueRow, 'stocked_at' | 'packed_at'>, now: number = Date.now()): number {
  const start = ageStart(p)
  if (!start) return 0
  return (now - new Date(start).getTime()) / 3_600_000
}

/** The age limit: 48 h for an export pallet, 30 days for local fruit. */
export function ageLimitHours(isExport: boolean): number {
  return isExport ? 48 : 720
}

export function fmtAge(hours: number): string {
  if (hours < 1)  return `${Math.round(hours * 60)}m`
  if (hours < 24) return `${Math.round(hours)}h`
  return `${(hours / 24).toFixed(1)}d`
}

// ---------------------------------------------------------------------------
// Find a pallet (UF-02)
// ---------------------------------------------------------------------------

/**
 * The SSCC a scanned or typed label carries, when it IS one (18 digits with a
 * good check digit, after the scanner's ']C1' and the '(00)' are stripped) —
 * else null, and the text is matched as a pallet number.
 */
export function ssccOf(text: string): string | null {
  const s = normaliseSscc(text)
  return isValidSscc(s) ? s : null
}

/**
 * Pallets a Find query names. A query matches a pallet whose number IS it (any
 * case — a pallet-id label scans as the number), or whose number ENDS with it —
 * the last digits of the number on the label, which is what a clerk reads off
 * a pallet at arm's length. One character is not a search: every number ends
 * in a digit.
 */
export function findInQueue(rows: readonly Pick<QueueRow, 'pallet_id'>[], query: string): string[] {
  const q = query.trim().toUpperCase()
  if (q.length < 2) return []
  return rows
    .filter(p => {
      const id = p.pallet_id.toUpperCase()
      return id === q || id.endsWith(q)
    })
    .map(p => p.pallet_id)
}

/** Bring the named pallets to the top, the rest in their order. Stable. */
export function matchesFirst<T extends Pick<QueueRow, 'pallet_id'>>(rows: readonly T[], matched: readonly string[]): T[] {
  if (matched.length === 0) return [...rows]
  const hit = new Set(matched)
  return [...rows.filter(p => hit.has(p.pallet_id)), ...rows.filter(p => !hit.has(p.pallet_id))]
}

/**
 * A scan, as a keyboard-wedge reader types it: 5+ characters with no space,
 * ended by the reader's Enter. No pallet-number format is assumed (numbering
 * is per business) — the same rule as the cold room's Find.
 */
export function scanShape(buffer: string): string | null {
  const t = buffer.trim()
  return t.length >= 5 && !/\s/.test(t) ? t : null
}

const VIEW_NAME: Record<QueueView, string> = { inspect: 'To inspect', ppecb: 'With PPECB', approved: 'Approved' }

/**
 * What the line under the Find box says about pallets the query names on the
 * OTHER views — "not here" alone sends the clerk looking for a pallet that was
 * approved an hour ago.
 */
export function elsewhereFound(rows: readonly QueueRow[], ids: readonly string[]): Array<{ id: string; view: QueueView; viewName: string }> {
  const byId = new Map(rows.map(p => [p.pallet_id, p]))
  const out: Array<{ id: string; view: QueueView; viewName: string }> = []
  for (const id of ids) {
    const p = byId.get(id)
    if (!p) continue
    const view = queueViewOf(p)
    if (view !== 'inspect') out.push({ id, view, viewName: VIEW_NAME[view] })
  }
  return out
}

// ---------------------------------------------------------------------------
// Hand-offs (UF-04)
// ---------------------------------------------------------------------------

/** A pallet number as the platform mints it: PREFIX-YYYY-serial (KF49-2026-00709). */
const PALLET_NUMBER = /\b[A-Z][A-Z0-9]*-\d{4}-\d+\b/gi

/**
 * The pallet a PPECB validation error is about, when its text quotes one —
 * else null. PPECB reports errors against its own line index
 * (`consignmentLines[1]…`), and the submission's line order is not stored, so
 * an index is never mapped back to a pallet. Only what the words really
 * quote counts: a pallet number (PREFIX-YYYY-serial, any case) or an SSCC
 * with a good check digit, each resolved against `rows` — the consignment's
 * own pallets. A quoted number or SSCC that is not one of them names nothing,
 * and so does an error about the booking or the exporter.
 */
export function errorPallet(
  err: { property?: string; message?: string },
  rows: readonly { pallet_id: string; ssc_id?: string | null }[],
): string | null {
  const text = `${err.property ?? ''} ${err.message ?? ''}`
  const byNumber = new Map(rows.map(p => [p.pallet_id.toUpperCase(), p.pallet_id]))
  for (const token of text.match(PALLET_NUMBER) ?? []) {
    const id = byNumber.get(token.toUpperCase())
    if (id) return id
  }
  const bySscc = new Map(rows.filter(p => p.ssc_id).map(p => [normaliseSscc(p.ssc_id), p.pallet_id]))
  // Each whole digit run: a printed '(00)' splits the AI off, and an 18-digit
  // slice of a longer number is not a quoted SSCC.
  for (const run of text.match(/\d+/g) ?? []) {
    const sscc = normaliseSscc(run)
    const id = isValidSscc(sscc) ? bySscc.get(sscc) : undefined
    if (id) return id
  }
  return null
}

/** The tick after a PPECB submission: "Sent to PPECB as CN-000186 · 4 pallets · UK". */
export function sentLabel(
  res: { consignment_number?: string | null; pallet_count?: number | null } | undefined,
  fallbackCount: number,
  market: string | null,
): string {
  const cn = res?.consignment_number
  const parts = [cn ? `Sent to PPECB as ${cn}` : 'Sent to PPECB', pallets(res?.pallet_count ?? fallbackCount)]
  if (market && market !== '—') parts.push(market)
  return parts.join(' · ')
}

/**
 * A PPECB submission refused for a QC FAIL (submit-ppecb-consignment answers
 * 409 with `blocking_runs`): which of the selected pallets are held — their
 * own run is one of the blocking runs — and which may go on their own
 * (F03 UF-05). A pallet held only through a build-up line can't be told from
 * here; it stays in `rest`, and the server refuses it again if so.
 */
export function splitQcHeld(
  palletIds: readonly string[],
  runOf: ReadonlyMap<string, string | null | undefined>,
  blockingRuns: readonly string[],
): { held: string[]; rest: string[] } {
  const blocking = new Set(blockingRuns)
  const held: string[] = []
  const rest: string[] = []
  for (const id of palletIds) {
    const run = runOf.get(id)
    ;(run && blocking.has(run) ? held : rest).push(id)
  }
  return { held, rest }
}

/** A refused PPECB submission, as the error the Send to PPECB box shows. */
export type SubmitRefusal = Error & { blockingRuns?: string[] }

/**
 * The error a failed submit-ppecb-consignment call throws. It carries PPECB's
 * own detail (or the auth/server detail) under the top-level error, so the
 * operator can see a field-name mismatch rather than just "HTTP 500". A 4xx
 * is a ValidationError (handled, not reported); a QC-FAIL refusal also names
 * the runs that block it (F03 UF-05), for splitQcHeld.
 */
export function ppecbSubmitError(
  status: number,
  body: { error?: string; detail?: unknown; blocking_runs?: unknown } | null | undefined,
): SubmitRefusal {
  const top    = body?.error ?? `HTTP ${status}`
  const detail = body?.detail
    ? (typeof body.detail === 'string' ? body.detail : JSON.stringify(body.detail))
    : null
  const msg = detail ? `${top}\n${detail}` : top
  if (status < 400 || status >= 500) return new Error(msg)
  const err: SubmitRefusal = new ValidationError(msg)
  if (Array.isArray(body?.blocking_runs)) err.blockingRuns = body.blocking_runs as string[]
  return err
}

/** What a rejected card says: the pallet, its run, and where it goes next. */
export function rejectedLine(palletId: string, runNumber: string | null | undefined): string {
  return `${palletId} rejected${runNumber ? ` · run ${runNumber}` : ''} — it goes to repack`
}

/** The Approved list's view chips (U19 UF-04). */
export type ApprovedFilter = 'all' | 'notruck' | 'near'
export const APPROVED_FILTERS: readonly ApprovedFilter[] = ['all', 'notruck', 'near']

/**
 * Which approved pallets each chip keeps. A set is undefined when its read has
 * not answered for EVERY approved pallet: an unread pallet is neither "on a
 * truck" nor "fresh", so the chip shows a dash instead of a guess.
 * Near the limit = the age chip's own test (amber or past the limit), and like
 * that chip it counts export pallets only.
 */
export function approvedFilterSets(
  approved: ReadonlyArray<QueueRow>,
  truckPlans: ReadonlyMap<string, { consignment: string | null; mission: boolean }> | undefined,
  dates: ReadonlyMap<string, { inspected_at: string | null; commodity: string | null }> | undefined,
  now: number = Date.now(),
): { notruck: Set<string> | undefined; near: Set<string> | undefined } {
  const whole = <V,>(m: ReadonlyMap<string, V> | undefined) =>
    m !== undefined && approved.every(p => m.has(p.pallet_id)) ? m : undefined
  const tp = whole(truckPlans)
  const dt = whole(dates)
  const none = approved.length === 0
  return {
    notruck: none ? new Set() : tp && new Set(approved.filter(p => {
      const t = tp.get(p.pallet_id); return !!t && !t.consignment && !t.mission
    }).map(p => p.pallet_id)),
    near: none ? new Set() : dt && new Set(approved.filter(p => {
      const d = dt.get(p.pallet_id)
      if (!d || p.channel !== 'EXPORT') return false
      const k = inspectionAge(d.inspected_at, d.commodity, now).kind
      return k === 'amber' || k === 'stale'
    }).map(p => p.pallet_id)),
  }
}

/** The cards a chip keeps, in the order given. A chip whose read is unanswered keeps every card (never hides on a guess). */
export function filterApproved<T extends { pallet_id: string }>(
  approved: readonly T[], filter: ApprovedFilter, sets: ReturnType<typeof approvedFilterSets>,
): T[] {
  if (filter === 'all') return [...approved]
  const keep = filter === 'notruck' ? sets.notruck : sets.near
  return keep ? approved.filter(p => keep.has(p.pallet_id)) : [...approved]
}
